import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { getTemplateCategories, getTemplatesByCategory, getAllTemplates } from '../../../templates/index';
import { getReportTypeFromTemplateName } from '../../../utils/reportFieldConfig';
import { buildReportRequestParams } from '../lib/reportRequest';
import { buildReportEnvelope, createCancellableReportFetch, newCancelToken } from '../lib/generateReport';
import { useEscapeKey } from '../lib/useEscapeKey';
import { filterTemplates, groupByCategory } from '../lib/templateSearch';
import { addRecentReport } from '../lib/recentReports';
import { rememberGeneratedDates } from '../lib/lastGeneratedDates';
import SavedConfigsRail from '../components/SavedConfigsRail';
import {
  LineChart,
  BarChart3,
  Building,
  TrendingUp,
  Activity,
  Package,
  Zap,
  DollarSign,
  Rocket,
  FileText,
  ArrowLeft
} from 'lucide-react';
import ReportConfigDialog from '../components/ReportConfigDialog';
import { useReportWindows } from '../windows/ReportWindowsContext';

const NAVY = '#0A0A0A';
const NAVY_SOFT = '#141414';
const CREAM = '#FFFFFF';
const GOLD = '#FFFFFF';
const HAIRLINE = 'rgba(255, 255, 255, 0.10)';
const HAIRLINE_STRONG = 'rgba(255, 255, 255, 0.22)';

const ReportsPage = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openReportWindow } = useReportWindows();
  const [selectedReportCategory, setSelectedReportCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showReportPopup, setShowReportPopup] = useState(false);
  // Client-side template search (name/description, case-insensitive) across
  // every category. Non-empty query replaces the category-cards grid with
  // grouped matches; clearing it returns to normal category browsing.
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');

  // Get client info from URL params (if navigated from portfolio page or chat)
  const clientId = searchParams.get('client_id');
  const clientName = searchParams.get('client_name') ? decodeURIComponent(searchParams.get('client_name')) : null;
  // report_type deep-link (chat-to-report handoff): select that template + open.
  const reportType = searchParams.get('report_type');

  // Auto-open the generation popup from URL params. A report_type selects the
  // matching template and opens it (prefilled by client_id/client_name); the
  // clientName branch opens an already-selected template's popup.
  useEffect(() => {
    if (reportType && !selectedTemplate) {
      const match = getAllTemplates().find(
        (t) => getReportTypeFromTemplateName(t.name) === reportType
      );
      if (match) {
        setSelectedTemplate(match);
        setShowReportPopup(true);
        return;
      }
    }
    if (selectedTemplate && clientName && !showReportPopup) {
      setShowReportPopup(true);
    }
  }, [reportType, selectedTemplate, clientName, showReportPopup]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Portfolio":
        return <Package className="w-8 h-8" />;
      case "Management":
        return <Building className="w-8 h-8" />;
      case "Fixed Income":
        return <BarChart3 className="w-8 h-8" />;
      case "Performance":
        return <TrendingUp className="w-8 h-8" />;
      case "Equity":
        return <LineChart className="w-8 h-8" />;
      case "Real Estate":
        return <Building className="w-8 h-8" />;
      case "Private Equity":
        return <TrendingUp className="w-8 h-8" />;
      case "Hedge Funds":
        return <Activity className="w-8 h-8" />;
      case "Commodities":
        return <Package className="w-8 h-8" />;
      case "Infrastructure":
        return <Zap className="w-8 h-8" />;
      case "Currencies":
        return <DollarSign className="w-8 h-8" />;
      case "Venture Capital":
        return <Rocket className="w-8 h-8" />;
      case "Alternative Investments":
        return <FileText className="w-8 h-8" />;
      default:
        return <FileText className="w-8 h-8" />;
    }
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setShowReportPopup(true);
  };

  const handleCloseReportPopup = useCallback(() => {
    // Does NOT cancel any in-flight generation: once Generate is clicked, the
    // native report popup owns cancellation (closing it while loading triggers
    // the per-generation `onCancel` closure built inside handleGenerateReport,
    // via openReportWindow below). This config popup closes itself synchronously
    // inside handleGenerateReport, so by the time anything calls
    // handleCloseReportPopup again there is nothing left for it to legitimately
    // cancel.
    setShowReportPopup(false);
    // Clear URL params when closing
    if (clientId || clientName) {
      navigate('/reports', { replace: true });
    }
  }, [clientId, clientName, navigate]);

  // Esc closes the report-generation popup (parity with the X / Cancel button).
  // Document-level via the hook so it works wherever focus sits inside the dialog.
  useEscapeKey(handleCloseReportPopup, showReportPopup);

  const handleGenerateReport = (params) => {
    if (!selectedTemplate) return;
    const reportType = getReportTypeFromTemplateName(selectedTemplate.name);
    const reportName = selectedTemplate.name || 'Report';

    // Per-generation cancellation - NOT shared. Several native report windows can
    // be generating at once, so each Generate click takes its own pair; a shared
    // one let closing window A abort whichever generation was current (B's, if B
    // started later) while A's query kept running server-side.
    const { fetchReport, cancel } = createCancellableReportFetch();

    openReportWindow({
      reportName,
      onCancel: cancel,
      produce: async ({ onProgress }) => {
        const requestParams = buildReportRequestParams(params, { reportType, clientId });
        rememberGeneratedDates(reportType, requestParams);
        // onProgress only fires on the async (group) path; the popup shows a
        // plain spinner for the sync path, which finishes in seconds.
        const result = await fetchReport(reportType, requestParams, {
          token: newCancelToken(), onProgress,
        });
        const reportDataWithOptions = buildReportEnvelope(result, requestParams);
        addRecentReport({ reportType, reportName, requestParams });
        return { reportType, reportName, reportData: reportDataWithOptions, requestParams };
      },
    }).catch((err) => {
      if (err && err.message === 'POPUP_BLOCKED') {
        window.alert('Your browser blocked the report window. Allow pop-ups for this site and click Generate again.');
      }
      // Other errors already render in the popup's error state.
    });

    setShowReportPopup(false); // config popup dismissed; the native popup is the surface
  };

  const textPrimary = darkMode ? CREAM : NAVY;
  const textMuted = darkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)';
  const hairline = darkMode ? HAIRLINE : 'rgba(0,0,0,0.10)';
  const hairlineStrong = darkMode ? HAIRLINE_STRONG : 'rgba(0,0,0,0.22)';
  const cardBg = darkMode
    ? `linear-gradient(145deg, ${NAVY_SOFT} 0%, ${NAVY} 100%)`
    : `linear-gradient(145deg, rgba(10,23,51,0.04) 0%, rgba(10,23,51,0.02) 100%)`;
  const iconBg = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(10,23,51,0.06)';

  // Client-side search across every category's templates (name/description).
  // Non-empty query -> grouped matches replace the category-cards grid below.
  const trimmedSearch = templateSearchQuery.trim();
  const searchResults = trimmedSearch ? groupByCategory(filterTemplates(getAllTemplates(), trimmedSearch)) : null;

  return (
    <div style={{ height: '100%', overflowY: 'auto', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ padding: 24 }}>
        {!selectedReportCategory ? (
          <div>
            {/* Header */}
            <div style={{ marginBottom: 28 }}>
              <h1
                className="font-display"
                style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: 400,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  color: textPrimary,
                  margin: 0,
                }}
              >
                Report Categories
              </h1>
            </div>

            {/* Template search: filters all templates by name/description across
                every category, case-insensitive, client-side only. */}
            <div style={{ marginBottom: 20, maxWidth: 420 }}>
              <input
                type="text"
                value={templateSearchQuery}
                onChange={(e) => setTemplateSearchQuery(e.target.value)}
                placeholder="Search reports..."
                aria-label="Search reports"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 4,
                  border: `1px solid ${hairline}`,
                  background: darkMode ? NAVY_SOFT : '#FFFFFF',
                  color: textPrimary,
                  fontSize: 14,
                  fontFamily: 'Inter, sans-serif',
                  outline: 'none',
                }}
              />
            </div>

            {searchResults ? (
              searchResults.length === 0 ? (
                <p style={{ fontSize: 14, color: textMuted }}>
                  No templates match &quot;{trimmedSearch}&quot;.
                </p>
              ) : (
                searchResults.map(({ category, items }) => (
                  <div key={category} style={{ marginBottom: 24 }}>
                    <h3 style={{ fontSize: 13, fontWeight: 500, color: textMuted, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {category}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
                      {items.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleTemplateClick(template)}
                          style={{
                            padding: '16px 18px',
                            borderRadius: 4,
                            border: `1px solid ${hairline}`,
                            background: cardBg,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          <h3 style={{ fontSize: 14, fontWeight: 500, color: textPrimary, margin: '0 0 6px', lineHeight: 1.3 }}>
                            {template.name}
                          </h3>
                          {template.description && (
                            <p style={{ fontSize: 13, color: textMuted, margin: 0, lineHeight: 1.5 }}>
                              {template.description}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )
            ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
              {getTemplateCategories().map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedReportCategory(category)}
                  style={{
                    padding: 24,
                    borderRadius: 4,
                    border: `1px solid ${hairline}`,
                    background: cardBg,
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'border-color 200ms, transform 200ms, box-shadow 200ms',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = darkMode ? HAIRLINE_STRONG : 'rgba(0,0,0,0.22)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = darkMode
                      ? '0 12px 32px -8px rgba(0,0,0,0.5)'
                      : '0 8px 24px -8px rgba(10,23,51,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = hairline;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Gold top line accent */}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 1,
                    background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
                    opacity: 0.4,
                  }} />
                  <div
                    style={{
                      width: 48, height: 48, margin: '0 auto 16px',
                      borderRadius: 9999,
                      background: iconBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: GOLD,
                    }}
                  >
                    {getCategoryIcon(category)}
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 500, color: textPrimary, margin: '0 0 6px' }}>
                    {category}
                  </h3>
                  <p style={{ fontSize: 13, color: textMuted, margin: 0 }}>
                    {getTemplatesByCategory(category).length} report
                    {getTemplatesByCategory(category).length !== 1 ? 's' : ''}
                  </p>
                </button>
              ))}
            </div>
            )}

            <SavedConfigsRail darkMode={darkMode} />
          </div>
        ) : (
          <div>
            {/* Back button */}
            <div style={{ marginBottom: 24 }}>
              <button
                onClick={() => setSelectedReportCategory(null)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px',
                  borderRadius: 9999,
                  border: `1px solid ${hairlineStrong}`,
                  background: 'transparent',
                  color: textMuted,
                  fontSize: 14,
                  fontFamily: 'Inter, sans-serif',
                  cursor: 'pointer',
                  transition: 'border-color 150ms, color 150ms',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = GOLD;
                  e.currentTarget.style.color = GOLD;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = hairlineStrong;
                  e.currentTarget.style.color = textMuted;
                }}
              >
                <ArrowLeft size={13} />
                Back to Categories
              </button>
            </div>

            {/* Category header */}
            <div style={{ marginBottom: 24 }}>
              <h1
                className="font-display"
                style={{
                  fontSize: 'clamp(1.375rem, 2.5vw, 2rem)',
                  fontWeight: 400,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  color: textPrimary,
                  margin: 0,
                }}
              >
                {selectedReportCategory} Reports
              </h1>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {getTemplatesByCategory(selectedReportCategory).map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateClick(template)}
                  style={{
                    padding: '20px 20px',
                    borderRadius: 4,
                    border: `1px solid ${hairline}`,
                    background: cardBg,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'border-color 200ms, transform 200ms, box-shadow 200ms',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = darkMode ? HAIRLINE_STRONG : 'rgba(0,0,0,0.22)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = darkMode
                      ? '0 10px 28px -8px rgba(0,0,0,0.45)'
                      : '0 6px 20px -8px rgba(10,23,51,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = hairline;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 1,
                    background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
                    opacity: 0.3,
                  }} />
                  <FileText size={20} style={{ color: GOLD, marginBottom: 12 }} />
                  <h3 style={{ fontSize: 14, fontWeight: 500, color: textPrimary, margin: '0 0 6px', lineHeight: 1.3 }}>
                    {template.name}
                  </h3>
                  {template.description && (
                    <p style={{ fontSize: 13, color: textMuted, margin: 0, lineHeight: 1.5 }}>
                      {template.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report Generation Popup */}
      {showReportPopup && selectedTemplate && (
        <ReportConfigDialog
          reportType={getReportTypeFromTemplateName(selectedTemplate.name)}
          reportName={selectedTemplate.name}
          reportDescription={selectedTemplate.description}
          isOpen={showReportPopup}
          onClose={handleCloseReportPopup}
          onConfirm={handleGenerateReport}
          prefillClientName={clientName}
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

export default ReportsPage;
